import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

import {
  PoPageModule,
  PoPageAction,
  PoInfoModule,
  PoInfoOrientation,
  PoDividerModule,
  PoFieldModule,
  PoButtonModule,
  PoWidgetModule,
  PoTableModule,
  PoTableColumn,
  PoTableAction,
  PoTagModule,
  PoModalModule,
  PoModalComponent,
  PoModalAction,
  PoSelectOption,
  PoLoadingModule,
  PoNotificationService,
  PoDialogService
} from '@po-ui/ng-components';

import { Subject, of, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';

import { Anexo, AnexosService, AnexoTipo, ChaveNota, GedFolderCheck, gedIndisponivelMsg } from './anexos.service';
import { toIsoDateLocal } from '../../shared/date-utils';
import { limparNumeroNf } from '../../shared/numero-utils';
import { CnaeOption, NotasAdicionarService } from '../notas-adicionar/notas-adicionar.service';
import { NotaDetalheService, NotaDetalheResp, ResolverProcessoResp, AprovarNotaItem, CteTesOpcao, ReprovarPendenteReq, tesSugeridaAol, IntegracaoConexosResp } from './nota-detalhe.service';
import { DictsOcultoService, DictsOculto, DictOption, DictProdutoOculto } from './dicts-oculto.service';
import { FilialService } from '../../core/services/filial.service';

interface NotaDetalhe {
  tipo: string;
  serie: string;
  numero: string;
  emissao: string;
  servico: string;
  origem: string;
  emissor: {
    razaoSocial: string;
    nomeFantasia: string;
    endereco: string;
    municipio: string;
    cep: string;
    cnpj: string;
    bairro: string;
    pais: string;
    complemento: string;
  };
  tomador: {
    razaoSocial: string;
    nomeFantasia: string;
    endereco: string;
    municipio: string;
    cep: string;
    cnpj: string;
    bairro: string;
    pais: string;
    complemento: string;
  };
  impostos: {
    pis: number;
    cofins: number;
    ir: number;
    inss: number;
    csll: number;
    total: number;
  };
  observacoes: string;
  observacoesAdicionais: string;
  historico: { data: string; hora: string; acao: string; usuario: string; descricao: string }[];
}

interface AnexoRow extends Anexo {
  tipoLabel: string;
  tamanhoLabel: string;
  dataUploadLabel: string;
}

interface PendingAnexo {
  tipo: AnexoTipo;
  tipoLabel: string;
  file: File;
  filename: string;
  tamanhoLabel: string;
  gedFolder: string;
}

@Component({
  selector: 'app-nota-detalhe',
  imports: [
    CommonModule,
    FormsModule,
    CurrencyPipe,
    PoPageModule,
    PoInfoModule,
    PoDividerModule,
    PoFieldModule,
    PoButtonModule,
    PoWidgetModule,
    PoTableModule,
    PoTagModule,
    PoModalModule,
    PoLoadingModule
  ],
  templateUrl: './nota-detalhe.html',
  styleUrls: ['./nota-detalhe.css'],
})
export class NotaDetalheComponent implements OnInit {
  readonly poInfoOrientation = PoInfoOrientation;

  @ViewChild('uploadModal', { static: true }) uploadModal!: PoModalComponent;
  @ViewChild('reprovarModal') reprovarModal!: PoModalComponent;

  // Chave da nota lida da rota
  chave: ChaveNota = { filOri: '', numero: '', serie: '', fornec: '', loja: '' };
  // NF-e (DS_NFELETR) recebida via queryParam - base para trocar o numero da
  // nota na aprovacao individual (limparNumeroNf), igual ao Executar Lote.
  private numeroNfe = '';
  // Emissao original vinda da listagem (queryParam, ISO yyyy-mm-dd). Guardada
  // separada de nota.emissao porque esta ultima e' sobrescrita pela resposta
  // do detalhe. Vai como discriminador na busca da nota: a chave
  // doc+serie+fornec+loja se repete quando a prefeitura reinicia a numeracao
  // da NFS-e por exercicio, e sem ela o backend devolvia a nota do ano errado.
  private emissaoChave = '';

  // Numero EXIBIDO: NF-e (DS_NFELETR) quando preenchida, senao o numero
  // (DS_DOC, inclusive os "T..."). Mesma regra de notas-list/notas-entregue.
  // SO exibicao - a chave da nota continua sendo nota.numero/chave.numero.
  get numeroExibe(): string {
    return limparNumeroNf(this.numeroNfe || (this.nota?.numero || ''));
  }

  // Anexos
  anexos: AnexoRow[] = [];
  loadingAnexos = false;
  uploadingAnexo = false;

  // Modal upload
  // Modal aceita multiplos arquivos por vez. Cada arquivo tem seu PROPRIO
  // tipo (DAN/BOL/ROM/CTE) selecionavel - o usuario pode subir um boleto
  // junto com a danfe no mesmo "Adicionar anexo" e taggear cada um.
  filesSelected: Array<{ file: File; tipo: AnexoTipo | '' }> = [];
  gedFolderSelected = '';
  gedFolderOptions: PoSelectOption[] = [];

  private readonly tipoOptionsAll: PoSelectOption[] = [
    { label: 'Danfe',    value: 'DAN' },
    { label: 'Boleto',   value: 'BOL' },
    { label: 'Romaneio', value: 'ROM' },
    { label: 'CTE',      value: 'CTE' }
  ];

  // Tipos disponiveis no modal de upload de anexo. Filtra BOL quando a
  // nota nao gera financeiro - boleto so faz sentido quando ha titulo
  // a pagar. Regra cliente 2026-05-29.
  // Memoizado (mesmo padrao do _tipoOperacaoOptions): o getter NAO pode
  // retornar uma ref nova a cada change detection. O .filter() criava um
  // array novo todo CD, e o po-select bindado em [p-options] re-sincronizava
  // e re-emitia p-change, mudando entry.tipo no meio do CD -> NG0100
  // (ExpressionChangedAfterItHasBeenChecked) e a selecao do tipo nao fixava.
  private _tipoOptions: PoSelectOption[] = [];
  private _tipoOptionsKey = '\0';   // sentinela: garante 1o calculo
  get tipoOptions(): PoSelectOption[] {
    const key = this.geraFinanceiro;            // 'sim' | 'nao' | ''
    if (key === this._tipoOptionsKey) {
      return this._tipoOptions;                 // mesma ref entre CDs
    }
    this._tipoOptionsKey = key;
    this._tipoOptions = key === 'sim'
      ? this.tipoOptionsAll
      : this.tipoOptionsAll.filter(o => o.value !== 'BOL');
    return this._tipoOptions;
  }

  // Staging local de anexos: arquivos selecionados antes da aprovacao.
  // Sao submetidos em sequencia logo apos onAprovar() retornar OK. Refresh
  // perde o staging (so vivem em memoria do componente).
  pendingAnexos: PendingAnexo[] = [];

  // Modo somente-leitura: quando true, esconde botoes de acao e desabilita
  // edicao dos campos. Setado via queryParam ?readonly=true ao abrir o
  // detalhe a partir da tela de notas entregues.
  isReadonly = false;

  // Form (mantido como stub)
  processo = '';
  geraFinanceiro = 'nao';
  tipoOperacao = '';
  produto = '';
  vencimento = '';

  // Resolvidos por GET /portalnf/notas-pendentes/detalhe + /portalnf/processo/resolver.
  tipoProcesso = '';
  natureza = '';
  bu = '';
  centroCusto = '';
  // Fluxo fornecedor oculto: usuario escolhe conta contabil (cascata
  // -> produtoOptionsOculto) ao inves do CNAE. Vazio em fluxos normais.
  contaContabil = '';
  priCod = '';
  // Simples Nacional do emitente (tag <indSN> do XML) - usado nas regras de CTe.
  private simplesNacional = false;
  // ICMS isento no CTe (grupo <ICMS45> CST 40/41 no XML) - usado pelo
  // autoPickTesCte para preferir a TES ISENTO (003/004) em vez da C/ICMS.
  private icmsIsento = false;
  // Pedagio do CTe (tag <comp><xNome>PEDAGIO</xNome><vComp>...) - quando >0,
  // o backend grava em F1_VALPEDG e deduz de D1_VUNIT/D1_TOTAL na aprovacao.
  // Publico para exibicao no template (widget "Informacoes de Prestacao de Servico").
  public valorPedagio = 0;
  carregandoDetalhe = false;
  resolvendoProcesso = false;
  aprovando = false;
  // Flag setada pelo resolverProcesso: bloqueia o botao Aprovar.
  processoEncerrado = false;
  // Data de encerramento (ISO yyyy-mm-dd) - usada na mensagem ao usuario.
  dataEncerramentoProcesso = '';
  // Flag do backend (ZOE) - quando true, libera o requisito de processo
  // obrigatorio na aprovacao. Setada por applyDetalhe.
  fornecedorOculto = false;
  // Flag do backend - true quando especie=CTe e tomador eh DAAS (CNPJ em
  // U_PnfDaasTomador). Habilita o radio "Sem processo" (Sim/Nao) abaixo
  // do campo Processo. Setada por applyDetalhe.
  tomadorDaas = false;

  // Habilita o radio "Sem processo". Resolvido pelo backend: privilegio
  // SEMPROC no menu /notas-list OU CTe de tomador DAAS. Substitui o teste
  // local (isCte && tomadorDaas), que restringia a opcao ao CTe da DAAS.
  // Chamado 00017826.
  podeSemProcesso = false;
  // Estado do radio "Sem processo": quando 'sim' o campo Processo eh
  // desabilitado/limpo, TES vai pra 403 e o payload de aprovar leva a
  // flag semProcesso=true (backend forca TES 403 + natureza 293014).
  semProcesso: 'sim' | 'nao' = 'nao';
  readonly semProcessoOptions: PoSelectOption[] = [
    { label: 'Sim', value: 'sim' },
    { label: 'Nao', value: 'nao' }
  ];
  private processo$ = new Subject<string>();
  comentario = '';

  readonly pageActions: Array<PoPageAction> = [
    { label: 'Voltar', action: () => this.voltar(), icon: 'an an-arrow-left' }
  ];

  // TES quando "Gera Financeiro" = sim (NFSe - familia "CF" com financeiro).
  // Lista derivada do de-para U_PnfTesNF (depara.tlpp):
  //   Terceiros + fin => issRet ? 256 : 257
  //   Encomenda/propria + fin => issRet ? 264 : 250
  readonly tipoOperacaoOptionsComFin: PoSelectOption[] = [
    { label: '(300) CF - AQUISICAO DE SERVICO SEM CRED E SEM RET ISS (sem processo)',            value: '300' },
    { label: '(257) CF - IPCOT AQUISICAO DE SERVICO SEM CRED E SEM RET ISS',                     value: '257' },
    { label: '(256) CF - IPCOT AQUISICAO DE SERVICO SEM CRED E COM RET ISS',                     value: '256' },
    { label: '(250) CF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E SEM RET ISS',         value: '250' },
    { label: '(264) CF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E COM RET ISS',         value: '264' }
  ];

  // TES quando "Gera Financeiro" = nao (NFSe - familia "SF" sem financeiro).
  readonly tipoOperacaoOptionsSemFin: PoSelectOption[] = [
    { label: '(300) CF - AQUISICAO DE SERVICO SEM CRED E SEM RET ISS (sem processo)',            value: '300' },
    { label: '(252) SF - IPCOT AQUISICAO DE SERVICO SEM CRED E SEM ISS COM RETIDOS',             value: '252' },
    { label: '(263) SF - IPCOT AQUISICAO DE SERVICO SEM CRED E COM RET ISS',                     value: '263' },
    { label: '(258) SF - IPCOT AQUISICAO DE SERVICO SEM CRED E SEM RET ISS',                     value: '258' },
    { label: '(261) SF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E SEM ISS COM RETIDOS', value: '261' },
    { label: '(251) SF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E COM RET ISS',         value: '251' },
    { label: '(255) SF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E SEM RET ISS',         value: '255' }
  ];

  // TES de CTe (frete) resolvidas pelo backend a partir da planilha
  // REGRAS CTE - filtradas por filial + processo + Simples Nacional.
  // Carregadas no detalhe/resolver; o filtro de "Gera Financeiro" e
  // aplicado no getter abaixo (via flag `dup`).
  cteTesOpcoes: CteTesOpcao[] = [];

  // Retorna a lista de TES coerente com o tipo da nota.
  //   - CTe: TODAS as opcoes vindas da planilha REGRAS CTE (sem filtrar por
  //     `dup`). O handler onTipoOperacaoChange sincroniza geraFinanceiro com
  //     o `dup` da TES escolhida (mesmo padrao do executar-lote).
  //   - NFSe/NFe: lista hardcoded filtrada por geraFinanceiro E pelo
  //     tipoProcesso (terceiros vs encomenda/propria) - sem processo,
  //     so' a TES generica "(sem processo)" (300).
  // Cacheada em `_tipoOperacaoOptions` para evitar nova referencia a cada
  // ciclo de change detection (po-select reage ao [p-options] mudando).
  // Lista de TES exibida no select. Cacheada por key (geraFin+tipoProcesso
  // ou cteTesOpcoes) - quando o key muda, gera nova referencia de array
  // (necessario para o po-select detectar a mudanca via ngOnChanges).
  private _tipoOperacaoOptions: PoSelectOption[] = [];
  private _tipoOperacaoOptionsKey = '';
  get tipoOperacaoOptions(): PoSelectOption[] {
    const isFin = this.geraFinanceiro === 'sim';
    const tp = (this.tipoProcesso || '').toLowerCase().trim();
    // Cache key: pra CTe inclui tipoOperacao porque a lista vira "1 opcao"
    // = a TES auto-escolhida; quando muda (autoPickTesCte), getter invalida.
    const key = this.isCte
      ? `cte:${this.semProcesso}:${this.tipoOperacao}:${this.cteTesOpcoes.length}`
      : `nfse:${isFin}:${tp}:${this.tesCalculada}:${this.tesCalculadaDesc}`;
    if (key === this._tipoOperacaoOptionsKey) {
      return this._tipoOperacaoOptions;
    }
    this._tipoOperacaoOptionsKey = key;
    if (this.isCte) {
      // CTe Sem Processo: usa as opcoes da planilha REGRAS CTE como CONTA E
      // ORDEM (cProc=C => 407/420 por filial, carregadas pelo
      // onSemProcessoChange via resolverProcesso). A TES 403 "FRETE - SEM
      // PROCESSO DAAS" so' fica como fallback quando a planilha nao tem linha
      // de Sem Processo pra filial (filial DAAS). Backend espelha em processaDoc.
      if (this.semProcesso === 'sim' && this.cteTesOpcoes.length === 0) {
        this._tipoOperacaoOptions = [{ label: '(403) FRETE - SEM PROCESSO DAAS', value: '403' }];
      // CTe: lista completa das opcoes vindas da planilha REGRAS CTE.
      // O autoPickTesCte sugere a inicial (com base em pedagio+geraFin),
      // mas o operador pode trocar (regra atualizada 2026-05). Cada opcao
      // tem `dup` que sincroniza geraFinanceiro via onTipoOperacaoChange.
      } else if (this.cteTesOpcoes.length > 0) {
        this._tipoOperacaoOptions = this.cteTesOpcoes.map(o => ({
          label: `(${o.tes}) ${o.desc}`,
          value: o.tes
        }));
      } else {
        this._tipoOperacaoOptions = [{ label: 'Preencha o processo para carregar a TES', value: '' }];
      }
    } else {
      const lista = isFin ? this.tipoOperacaoOptionsComFin : this.tipoOperacaoOptionsSemFin;
      const filtrada = this.filtrarTesPorTipoProcesso(lista, tp);
      // Excecao por filial: o backend aplicou o de-para e devolveu a TES junto
      // com a descricao da SF4. Sem descricao (TES sem cadastro na filial) nao
      // oferecemos a opcao - melhor faltar do que oferecer TES invalida.
      const jaNaLista = filtrada.some(o => o.value === this.tesCalculada);
      this._tipoOperacaoOptions = (!jaNaLista && this.tesCalculada && this.tesCalculadaDesc)
        ? [...filtrada, { label: `(${this.tesCalculada}) ${this.tesCalculadaDesc}`, value: this.tesCalculada }]
        : filtrada;
    }
    return this._tipoOperacaoOptions;
  }

  // Filtra a lista hardcoded de TES NFSe pelo tipo de processo resolvido em
  // SW2.W2_ZTPIMPO. Marcadores no label de cada TES:
  //   - "(sem processo)" -> TES 300 generica - APENAS quando processo vazio
  //   - "IPCOT"           -> familia terceiros (conta e ordem)
  //   - "ENCOMENDA/PROPRIA" -> familia encomenda/propria
  // Com processo preenchido, a TES (sem processo) eh excluida pra alinhar
  // com a logica do PHP (NfseOperation::getTes - so' devolve TES por
  // tripla processType+finance+issRet+otherRet, sem fallback generico).
  private filtrarTesPorTipoProcesso(lista: PoSelectOption[], tipoProcesso: string): PoSelectOption[] {
    if (!tipoProcesso) {
      // Sem processo: so' a TES generica "(sem processo)" - normalmente 300.
      // Fallback pra lista cheia se a generica nao existir na lista atual.
      const semProc = lista.filter(o => /\(sem processo\)/i.test(o.label));
      return semProc.length > 0 ? semProc : lista;
    }
    // Com processo: NUNCA inclui a TES (sem processo).
    const semGenerica = lista.filter(o => !/\(sem processo\)/i.test(o.label));
    if (tipoProcesso === 'terceiros') {
      return semGenerica.filter(o => /IPCOT/i.test(o.label));
    }
    if (tipoProcesso === 'encomenda' || tipoProcesso === 'propria') {
      return semGenerica.filter(o => /ENCOMENDA\/PROPRIA/i.test(o.label));
    }
    return semGenerica;
  }

  // Memoriza a TES escolhida (manual ou auto) por estado de geraFinanceiro.
  // Permite restaurar a TES original quando o usuario alterna sim/nao/sim
  // sem perder o trabalho. Populado em onTipoOperacaoChange e ao inicio de
  // onGeraFinanceiroChange (antes de trocar o estado).
  private tipoOperacaoMemo: { sim?: string; nao?: string } = {};

  // Sinaliza que o operador escolheu a TES manualmente no select. Setado no
  // handler (p-change) do po-select, que so dispara por interacao do usuario
  // (o template usa [ngModel] one-way, entao escritas programaticas de
  // tipoOperacao NAO disparam esse evento). Uma vez true, os recomputes
  // automaticos (applyDetalhe / resolverProcesso) param de sobrescrever a
  // escolha do operador - correcao do bug 258->252, em que o default 252
  // recalculado apagava silenciosamente a TES 258 escolhida a mao. O
  // syncTipoOperacao continua rodando e so corrige a TES se ela sair da
  // lista visivel (familia incompativel com o processo/geraFinanceiro).
  private tesEditadaManual = false;

  // TES calculada pelo backend e a descricao dela na SF4 da filial. A lista de
  // NFS-e do select e fixa aqui e nao conhece as excecoes por filial do de-para
  // (TES 248 na 006001008, chamado 00018693): quando a TES calculada nao esta
  // na lista, ela entra por estes dois campos.
  private tesCalculada = '';
  private tesCalculadaDesc = '';

  // Auto-escolhe a TES de CTe definitiva, baseado no contexto (geraFin +
  // valorPedagio + descricao). Operador NAO escolhe TES de CTe (regra do
  // cliente) - o template trava o select. Sempre roda para recalcular.
  // Heuristica:
  //   1) Pedagio: valorPedagio>0 -> so TES com "PEDAGIO"; senao, exclui
  //   2) Gera Financeiro: APENAS candidatos com dup === (geraFin==='sim').
  //      Se nao houver, deixa TES vazia (avisa via toast) - NUNCA
  //      sobrescreve geraFinanceiro do user com outro valor (causa loop
  //      de UI inconsistente quando ele clica Nao mas autoPick muda Sim).
  //   3) Prefere TES com marcador C/FINANCEIRO ou S/FINANCEIRO no desc
  //   4) ICMS: se icmsIsento (CST 40/41 no XML) -> prefere TES "ISENTO"
  //      (003/004); senao -> exclui "SEM ICMS"/"S/ICMS"/"ISENTO", fica C/ICMS
  //   5) Pega a primeira da lista filtrada
  private autoPickTesCte(): void {
    if (!this.isCte || !this.cteTesOpcoes.length) { return; }
    let candidatos = this.cteTesOpcoes.slice();

    const temPedagio = (this.valorPedagio || 0) > 0;
    const comPedagio = candidatos.filter(o => /pedagio/i.test(o.desc));
    const semPedagio = candidatos.filter(o => !/pedagio/i.test(o.desc));
    if (temPedagio && comPedagio.length > 0) { candidatos = comPedagio; }
    else if (!temPedagio && semPedagio.length > 0) { candidatos = semPedagio; }

    const wantDup = this.geraFinanceiro === 'sim';
    const dupMatch = candidatos.filter(o => o.dup === wantDup);
    if (dupMatch.length === 0) {
      // Nenhuma TES com dup compativel com o geraFinanceiro do user:
      // respeita a escolha do user (NAO sobrescreve geraFinanceiro) e
      // tambem PRESERVA a TES atual (regra cliente 2026-05-28) - so
      // avisa via toast pra ele saber que a combinacao eh inconsistente
      // com o cadastro. Antes zerava a TES, mas isso atrapalhava operacao.
      this.notif.warning(
        `Sem TES de CTe disponivel para ${wantDup ? '"com financeiro"' : '"sem financeiro"'} ` +
        `na filial ${this.chave.filOri} - mantida a TES atual.`
      );
      return;
    }
    candidatos = dupMatch;

    const explicitFin = candidatos.filter(o =>
      wantDup ? /c\/\s*financeiro/i.test(o.desc) : /s\/\s*financeiro/i.test(o.desc)
    );
    if (explicitFin.length > 0) { candidatos = explicitFin; }

    // ICMS isento (CST 40/41 no XML): prefere a TES ISENTO (003/004). Quando
    // NAO isento, exclui as isentas junto com as S/ICMS, mantendo a C/ICMS.
    // As TES ISENTO trazem "ISENTO" na descricao (aPnfMapTesCte, bloco 003/004).
    if (this.icmsIsento) {
      const isentoOpts = candidatos.filter(o => /isento/i.test(o.desc));
      if (isentoOpts.length > 0) { candidatos = isentoOpts; }
    } else {
      const semIcmsExc = candidatos.filter(o => !/sem\s*icms|s\/\s*icms|isento/i.test(o.desc));
      if (semIcmsExc.length > 0) { candidatos = semIcmsExc; }
    }

    // Operacao AOL da Logistica Matriz: a 424 vence a heuristica acima quando
    // o CTe e de um dos fornecedores da lista. Sugestao apenas - o operador
    // troca no select conforme a finalidade do frete (chamado 00018371).
    const tesAol = tesSugeridaAol(this.chave.filOri, this.chave.fornec, this.cteTesOpcoes);
    const escolhida = tesAol
      ? (candidatos.find(o => o.tes === tesAol)
         ?? this.cteTesOpcoes.find(o => o.tes === tesAol)!)
      : candidatos[0];
    this.tipoOperacao = escolhida.tes;
    // geraFinanceiro ja eh consistente (dup === wantDup), nao precisa setar
    // Invalida cache do tipoOperacaoOptions (key inclui tipoOperacao)
    this._tipoOperacaoOptionsKey = '';
  }

  // Handler do select TES.
  //   - CTe: sincroniza geraFinanceiro a partir do `dup` da TES escolhida
  //     (cada TES da planilha REGRAS CTE pertence a um regime fixo).
  //   - NFSe: apenas registra a TES.
  // Em ambos os casos memoriza a escolha para o geraFinanceiro corrente.
  onTipoOperacaoChange(v: string): void {
    console.log('onTipoOperacaoChange', v);
    //this.tipoOperacao = v;
    // CTe: ANTES (ate 2026-05-28) sincronizavamos geraFinanceiro com
    // TES.dup automaticamente. Removido em 2026-05-29: o sync forcado
    // sobrescrevia a escolha do user no radio - cenario reportado:
    // user marca "Gera Financeiro = Nao" mas o autoPick (ou troca
    // manual) sugere TES com dup=true, e o handler entao virava o radio
    // de volta pra "Sim" SEM ele perceber, disparando erros de boleto/
    // vencimento obrigatorios na aprovacao. geraFinanceiro agora eh
    // sempre o valor do radio - se houver inconsistencia TES vs radio,
    // o backend valida e devolve erro explicito.
    if (v) {
      this.tipoOperacaoMemo[this.geraFinanceiro as 'sim'|'nao'] = v;
      // NFSe: marca a escolha como manual pra protege-la dos recomputes
      // automaticos (applyDetalhe / resolverProcesso). CTe nao chega aqui por
      // escolha do operador - o select e' travado e a TES vem do autoPick.
      if (!this.isCte) { this.tesEditadaManual = true; }
    }
  }

  // CTe? (tipo vem do queryParam/readNota como 'CTe')
  get isCte(): boolean {
    return (this.nota?.tipo || '').toUpperCase() === 'CTE';
  }

  // Handler do radio "Sem processo" (CTe + tomador DAAS). Quando o operador
  // marca Sim: limpa o campo Processo e carrega as TES da planilha REGRAS CTE
  // como CONTA E ORDEM (cProc=C => 407/420 por filial). A natureza 293014
  // (frete) eh fixa pra CTe. A TES 403 deixou de ser fixa: so' aparece como
  // fallback (getter) quando a planilha nao tem linha de Sem Processo (filial
  // DAAS). Backend espelha em processaDoc. Quando volta pra Nao, re-roda o
  // autoPickTesCte normal.
  onSemProcessoChange(v: string): void {
    this.semProcesso = v as 'sim' | 'nao';
    if (v === 'sim') {
      this.processo     = '';
      this.tipoProcesso = '';
      this.tipoOperacao = '';
      // Invalida cache do getter tipoOperacaoOptions (chave inclui semProc)
      this._tipoOperacaoOptionsKey = '';
      if (this.isCte) {
        // CTe: natureza fixa de frete e TES vinda da planilha REGRAS CTE
        // (linha "S" da filial, ou fallback 403). Comportamento original.
        this.natureza = '293014';
        this.carregarTesSemProcesso();
      } else {
        // NFS-e sem processo (privilegio SEMPROC): a natureza continua sendo
        // calculada pelo backend a partir das retencoes - NAO e 293014, que e
        // natureza de frete. Com processo e tipoProcesso vazios, o
        // filtrarTesPorTipoProcesso passa a oferecer a TES generica
        // "(sem processo)" (300), mesma regra do U_PnfTesNF. Pre-seleciona a
        // 300 quando ela for a unica opcao, mas mantem o campo habilitado: o
        // backend RECUSA a entrega sem TES fora do CTe. Chamado 00017826.
        this.recalcularTesNfse();
        const opcoes = this.tipoOperacaoOptions;
        if (opcoes.length === 1 && opcoes[0].value) {
          this.tipoOperacao = String(opcoes[0].value);
        }
      }
    } else {
      this.tipoOperacao = '';
      this._tipoOperacaoOptionsKey = '';
      this.autoPickTesCte();
    }
  }

  // Carrega as TES de CTe "Sem Processo" da planilha REGRAS CTE. Sem um numero
  // de processo real, manda um placeholder + tipoProcesso='semprocesso'
  // (=> cProc=S no backend). O backend devolve a linha "S" propria da filial
  // (ex.: 403 na 006001007) ou, se a filial nao tiver linha "S", faz fallback
  // para as linhas "C" (Conta e Ordem) - retrocompativel. Se vier vazio (filial
  // DAAS sem C nem S), o getter cai no fallback 403.
  private carregarTesSemProcesso(): void {
    this.notaDetalheService.resolverProcesso({
      processo:     '_sem_processo_',
      filial:       this.chave.filOri,
      sn:           this.simplesNacional,
      tipoProcesso: 'semprocesso',
      fin:          this.geraFinanceiro === 'sim'
    }).subscribe({
      next: r => {
        this.cteTesOpcoes = r.tesCteOpcoes ?? [];
        this._tipoOperacaoOptionsKey = '';
        this.autoPickTesCte();
      },
      error: () => {
        this.cteTesOpcoes = [];
        this._tipoOperacaoOptionsKey = '';
      }
    });
  }

  readonly geraFinanceiroOptions: PoSelectOption[] = [
    { label: 'Sim', value: 'sim' },
    { label: 'Não', value: 'nao' },
  ];

  // Lista de produtos para NFSe (CNAE + descricao -> codigo do produto
  // Protheus). Carregada via GET /portalnf/notas/cnaes (U_PnfCnaeList) no
  // ngOnInit. Para CTe usamos cteProdutoOptions (frete).
  private cnaeProdutoOptions: PoSelectOption[] = [];
  private cnaeList: CnaeOption[] = [];
  carregandoProdutos = false;

  // Opcoes fixas de "Produto" para CTe (frete) - decididas pela tag <indSN>
  // do XML (via U_PnfPrdCte). O backend pre-seleciona pela tag; o usuario
  // pode trocar manualmente se precisar.
  readonly cteProdutoOptions: PoSelectOption[] = [
    { label: '000000000020475 - FRETE S/ VENDA (SIMPLES NACIONAL)', value: '000000000020475' },
    { label: '000000000000263 - FRETE S/ VENDA',                   value: '000000000000263' }
  ];

  // Retorna as opcoes de Produto coerentes com o tipo da nota.
  get produtoOptions(): PoSelectOption[] {
    return this.isCte ? this.cteProdutoOptions : this.cnaeProdutoOptions;
  }

  // CNAE da nota (vem do XML via queryParam). Usado para auto-selecionar
  // o produto quando a lista terminar de carregar.
  private cnaeNota = '';

  readonly historicoColumns: Array<PoTableColumn> = [
    { property: 'data',      label: 'Data',     width: '110px' },
    { property: 'hora',      label: 'Hora',     width: '90px'  },
    { property: 'acao',      label: 'Ação',     width: '180px' },
    { property: 'usuario',   label: 'Usuário' },
    { property: 'descricao', label: 'Detalhes' }
  ];

  readonly anexosColumns: Array<PoTableColumn> = [
    { property: 'tipoLabel',       label: 'Tipo',     width: '110px' },
    { property: 'filename',        label: 'Arquivo' },
    { property: 'tamanhoLabel',    label: 'Tamanho',  width: '100px' },
    { property: 'dataUploadLabel', label: 'Upload',   width: '160px' },
    { property: 'usuario',         label: 'Usuário',  width: '100px' }
  ];

  anexosActions: Array<PoTableAction> = [
    { label: 'Baixar',  icon: 'an an-download-simple', action: (row: AnexoRow) => this.baixarAnexo(row) },
    { label: 'Excluir', icon: 'po-icon-delete', type: 'danger', action: (row: AnexoRow) => this.confirmarExcluir(row) }
  ];

  readonly pendingAnexosColumns: Array<PoTableColumn> = [
    { property: 'tipoLabel',    label: 'Tipo',     width: '110px' },
    { property: 'filename',     label: 'Arquivo' },
    { property: 'tamanhoLabel', label: 'Tamanho',  width: '100px' }
  ];

  readonly pendingAnexosActions: Array<PoTableAction> = [
    { label: 'Remover', icon: 'po-icon-delete', type: 'danger', action: (row: PendingAnexo) => this.removerPendente(row) }
  ];

  readonly modalUploadPrimary: PoModalAction = {
    action: () => this.salvarUpload(),
    label: 'Enviar'
  };

  readonly modalUploadSecondary: PoModalAction = {
    action: () => this.uploadModal.close(),
    label: 'Cancelar'
  };

  // ----- Reprovar pre-nota -----
  // motivoReprovacao vai pra ZOH_OBS via historico; reprovando bloqueia o
  // botao enquanto a request roda.
  motivoReprovacao = '';
  reprovando = false;

  readonly modalReprovarConfirma: PoModalAction = {
    label: 'Confirmar reprovacao',
    action: () => this.confirmarReprovar(),
    danger: true
  };

  readonly modalReprovarCancela: PoModalAction = {
    label: 'Cancelar',
    action: () => this.fecharReprovar()
  };

  // Stub de dados estaticos (substituido quando endpoint de detalhe existir)
  nota: NotaDetalhe = {
    tipo: 'NFSe',
    serie: '',
    numero: '',
    emissao: '-',
    servico: '-',
    origem: 'Transmite',
    emissor: { razaoSocial: '-', nomeFantasia: '', endereco: '-', municipio: '-', cep: '-', cnpj: '-', bairro: '-', pais: '', complemento: '-' },
    tomador: { razaoSocial: '-', nomeFantasia: '', endereco: '-', municipio: '-', cep: '-', cnpj: '-', bairro: '-', pais: '', complemento: '-' },
    impostos: { pis: 0, cofins: 0, ir: 0, inss: 0, csll: 0, total: 0 },
    observacoes: '-',
    observacoesAdicionais: '',
    historico: []
  };

  // Situacao da integracao da NFS-e com o Conexos (bloco somente leitura;
  // o reprocesso fica na tela de Notas Entregues). null = bloco escondido.
  cnxIntegracao: IntegracaoConexosResp | null = null;

  // Listas para fluxo de fornecedor oculto (carregadas sob demanda).
  ocultoContaOptions: DictOption[] = [];
  ocultoBuOptions: DictOption[] = [];
  ocultoCcOptions: DictOption[] = [];
  ocultoProdutoOptions: DictOption[] = [];
  private ocultoProdutos: DictProdutoOculto[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private anexosService: AnexosService,
    private notasAdicionarService: NotasAdicionarService,
    private notaDetalheService: NotaDetalheService,
    private dictsOcultoService: DictsOcultoService,
    private filialService: FilialService,
    private notif: PoNotificationService,
    private dialog: PoDialogService
  ) {}

  ngOnInit(): void {
    // Dicionarios usados quando o fornecedor eh oculto (Conta Contabil,
    // BU, CC, Produto). Cache em memoria - carrega uma vez por sessao.
    this.dictsOcultoService.load().subscribe({
      next: (d: DictsOculto) => {
        this.ocultoContaOptions = d.contas;
        this.ocultoBuOptions    = d.bus;
        this.ocultoCcOptions    = d.centrosCusto;
        this.ocultoProdutos     = d.produtos;
        if (this.contaContabil) {
          this.refreshProdutoOcultoOptions();
        }
      },
      error: () => {}
    });

    // Lista de pastas GED disponiveis no upload de anexos
    this.anexosService.gedFolders().subscribe({
      next: pastas => {
        this.gedFolderOptions = pastas.map(p => ({ label: p, value: p }));
        if (!this.gedFolderSelected && this.gedFolderOptions.length > 0) {
          this.gedFolderSelected = this.gedFolderOptions[0].value as string;
        }
      },
      error: () => {
        // Fallback se o endpoint falhar - mesmas opcoes default do backend.
        this.gedFolderOptions = [
          { label: 'DESPESAS PAGAS',                value: 'DESPESAS PAGAS' },
          { label: 'DESPESAS PAGAS PELO CLIENTE',   value: 'DESPESAS PAGAS PELO CLIENTE' }
        ];
        this.gedFolderSelected = 'DESPESAS PAGAS';
      }
    });

    const p = this.route.snapshot.paramMap;
    const q = this.route.snapshot.queryParamMap;

    // Modo somente-leitura: NF ja entregue. Esconde botoes de acao,
    // remove acao de excluir anexo e desabilita campos do formulario.
    this.isReadonly = q.get('readonly') === 'true';
    if (this.isReadonly) {
      this.anexosActions = this.anexosActions.filter(a => a.label !== 'Excluir');
    }

    // Chave da nota: numero vem do path; demais campos da chave vem dos
    // queryParams (passados pela tela de listagem que sabe a chave completa).
    this.chave = {
      filOri: q.get('filial') ?? '',
      numero: p.get('numero') ?? '',
      serie:  q.get('serie')  ?? '',
      fornec: q.get('fornec') ?? '',
      loja:   q.get('loja')   ?? ''
    };
    this.numeroNfe = q.get('numeroNfe') ?? '';

    this.nota.numero = this.chave.numero;
    this.nota.serie  = this.chave.serie;

    // Anexos: precisa da chave completa
    if (this.chave.filOri && this.chave.numero && this.chave.serie &&
        this.chave.fornec && this.chave.loja) {
      this.carregarAnexos();
    }

    // ----- Impostos detalhados -----
    const impostoPIS = q.get('impostoPIS');
    if (impostoPIS)    this.nota.impostos.pis    = Number(impostoPIS);
    const impostoCOFINS = q.get('impostoCOFINS');
    if (impostoCOFINS) this.nota.impostos.cofins = Number(impostoCOFINS);
    const impostoIR = q.get('impostoIR');
    if (impostoIR)     this.nota.impostos.ir     = Number(impostoIR);
    const impostoINSS = q.get('impostoINSS');
    if (impostoINSS)   this.nota.impostos.inss   = Number(impostoINSS);
    const impostoCSLL = q.get('impostoCSLL');
    if (impostoCSLL)   this.nota.impostos.csll   = Number(impostoCSLL);
    const emissao = this.route.snapshot.queryParamMap.get('emissao');
    if (emissao) {
      this.nota.emissao = emissao;
      this.emissaoChave = emissao;
    }
    const total = this.route.snapshot.queryParamMap.get('total');
    if (total) {
      this.nota.impostos.total = Number(total);
    }
    const qp = this.route.snapshot.queryParamMap;
    const tipoQp = qp.get('tipo');
    if (tipoQp) { this.nota.tipo = tipoQp; }
    const tomadorNome = qp.get('tomadorNome');
    if (tomadorNome) { this.nota.tomador.razaoSocial = tomadorNome; }
    const tomadorFantasia = qp.get('tomadorFantasia');
    if (tomadorFantasia) { this.nota.tomador.nomeFantasia = tomadorFantasia; }
    const tomadorCnpj = qp.get('tomadorCnpj');
    if (tomadorCnpj) { this.nota.tomador.cnpj = tomadorCnpj; }
    const tomadorEndereco = qp.get('tomadorEndereco');
    if (tomadorEndereco) { this.nota.tomador.endereco = tomadorEndereco; }
    const tomadorBairro = qp.get('tomadorBairro');
    if (tomadorBairro) { this.nota.tomador.bairro = tomadorBairro; }
    const tomadorMunicipio = qp.get('tomadorMunicipio');
    if (tomadorMunicipio) { this.nota.tomador.municipio = tomadorMunicipio; }
    const tomadorEstado = qp.get('tomadorEstado');
    if (tomadorEstado) {
      const parts = this.nota.tomador.municipio.split(' - ');
      if (parts.length === 2) {
        parts[1] = tomadorEstado;
        this.nota.tomador.municipio = parts.join(' - ');
      } else {
        this.nota.tomador.municipio = (this.nota.tomador.municipio || '') + ' - ' + tomadorEstado;
      }
    }
    const tomadorCep = qp.get('tomadorCep');
    if (tomadorCep) { this.nota.tomador.cep = tomadorCep; }
    const tomadorComplemento = qp.get('tomadorComplemento');
    if (tomadorComplemento) { this.nota.tomador.complemento = tomadorComplemento; }

    // ----- Formulario (Processo / Gera Financeiro / TES / Vencimento) -----
    const processoQp = qp.get('processo');
    if (processoQp) { this.processo = processoQp; }

    const finQp = qp.get('fin');
    if (finQp) {
      // Backend retorna 'S'/'N'; o radio do front usa 'sim'/'nao'
      const v = finQp.toUpperCase();
      this.geraFinanceiro = (v === 'S' || v === 'SIM') ? 'sim' : 'nao';
    }

    const tesQp = qp.get('tes');
    if (tesQp) {
      const padded = String(tesQp).padStart(3, '0');
      this.tipoOperacao = padded;
    }

    const vencimentoQp = qp.get('vencimento');
    if (vencimentoQp) { this.vencimento = vencimentoQp; }

    const cnaeQp = qp.get('cnae');
    if (cnaeQp) { this.cnaeNota = cnaeQp.trim(); }

    const produtoQp = qp.get('produto');
    if (produtoQp) { this.produto = produtoQp.trim(); }

    // Se o estado inicial (QP) deixou tipoOperacao invalido para a lista
    // atual (ex: gera-fin=sim sem tes), aplica o default (257 para sim).
    // PULA em modo readonly (notas-entregues) - nesse caso o usuario so
    // visualiza, os valores vem dos queryParams e nao devem ser alterados.
    // Sem esse skip, o syncTipoOperacao filtra com tipoProcesso='' (ainda
    // nao resolvido) e reseta a TES correta pra 300.
    if (!this.isReadonly) {
      this.aplicarTesDefault();
    }

    this.carregarProdutos();
    this.setupProcessoDebounce();
    this.loadDetalhe();
  }

  // ---------------------------------------------------------------------
  // Carregamento do detalhe (processo + cnae + tes + natureza + produto)
  // ---------------------------------------------------------------------

  private loadDetalhe(): void {
    if (!this.chave.filOri || !this.chave.numero || !this.chave.serie ||
        !this.chave.fornec || !this.chave.loja) {
      return;
    }
    this.carregandoDetalhe = true;
    this.notaDetalheService.detalhe({
      filial: this.chave.filOri,
      doc:    this.chave.numero,
      serie:  this.chave.serie,
      fornec: this.chave.fornec,
      loja:   this.chave.loja,
      // Discriminadores de numeracao repetida (ver emissaoChave). isReadonly
      // identifica a origem: readonly=true so vem de Notas Entregues (SF1);
      // caso contrario a nota veio de Notas Pendentes (pre-nota SDS).
      emissao: this.emissaoChave || undefined,
      origem:  this.isReadonly ? 'entregue' : 'pendente'
    }).subscribe({
      next: (d: NotaDetalheResp) => {
        this.carregandoDetalhe = false;
        this.applyDetalhe(d);
      },
      error: err => {
        this.carregandoDetalhe = false;
        const msg = err?.error?.message ?? 'Falha ao carregar detalhe da nota.';
        this.notif.warning(msg);
      }
    });
  }

  private applyDetalhe(d: NotaDetalheResp): void {
    if (d.processo)  { this.processo  = d.processo; }
    if (d.cnae)      { this.cnaeNota  = d.cnae; }
    if (d.cnaeNorm)  { this.nota.servico = d.cnaeNorm; }
    else if (d.cnae) { this.nota.servico = d.cnae; }
    if (d.produto)   { this.produto   = d.produto; }
    // Para CTe o `tes` do backend e regra NFSe - ignorado (a selecao vem
    // da lista filtrada de CTe; ver tipoOperacaoOptions / syncTipoOperacao).
    // Nao sobrescreve a TES se o operador ja escolheu manualmente (bug 258->252).
    this.tesCalculada = d.tes ? String(d.tes).padStart(3, '0') : '';
    this.tesCalculadaDesc = d.tesDesc ?? '';
    this._tipoOperacaoOptionsKey = '';
    if (d.tes && !this.isCte && !this.tesEditadaManual) { this.tipoOperacao = String(d.tes).padStart(3, '0'); }
    if (d.natureza)  { this.natureza  = d.natureza; }
    this.tipoProcesso = d.tipoProcesso ?? '';
    this.bu           = d.bu ?? '';
    this.centroCusto  = d.centroCusto ?? '';
    this.contaContabil = d.contaContabil ?? '';
    this.priCod       = d.priCod ?? '';
    this.simplesNacional = d.simplesNacional ?? false;
    this.icmsIsento      = d.icmsIsento ?? false;
    this.valorPedagio    = d.valorPedagio ?? 0;
    this.cteTesOpcoes    = d.tesCteOpcoes ?? [];
    this.fornecedorOculto = !!d.fornecedorOculto;
    this.tomadorDaas      = !!d.tomadorDaas;
    this.podeSemProcesso  = !!d.podeSemProcesso;
    // Fornecedor oculto: o po-select de Produto eh cascateado pela Conta
    // Contabil (ocultoProdutoOptions filtra produtos por conta). Sem
    // recarregar a cascata, o produto gravado em SD1 nao aparece visivel
    // no detalhe somente-leitura. Refresh aqui depois de setar a conta.
    if (this.fornecedorOculto && this.contaContabil) {
      this.refreshProdutoOcultoOptions();
    }
    // Historico (ZOH). 5 colunas no po-table: data + hora + acao (label
    // amigavel) + usuario (nome ZOA) + descricao (detalhes/contexto).
    this.nota.historico = (d.historico ?? []).map(h => ({
      data:      h.data,
      hora:      h.hora,
      acao:      h.acaoLabel || h.acao,
      usuario:   h.usuarioNom || h.usuario,
      descricao: h.descricao || h.obs || ''
    }));

    // Integracao com o Conexos: so exibe quando os campos existem e a nota
    // entrou no ciclo (status preenchido).
    const cnx = d.integracaoConexos;
    this.cnxIntegracao = (cnx && cnx.disponivel && (cnx.status ?? '') !== '') ? cnx : null;

    if (d.emit) {
      const e = d.emit;
      if (e.razao)   { this.nota.emissor.razaoSocial  = e.razao; }
      if (e.fant)    { this.nota.emissor.nomeFantasia = e.fant; }
      if (e.cnpjFmt) { this.nota.emissor.cnpj         = e.cnpjFmt; }
      else if (e.cnpj) { this.nota.emissor.cnpj       = e.cnpj; }
      if (e.end) {
        if (e.end.lgr)    { this.nota.emissor.endereco    = e.end.lgr + (e.end.nro ? ', ' + e.end.nro : ''); }
        if (e.end.bairro) { this.nota.emissor.bairro      = e.end.bairro; }
        if (e.end.mun)    { this.nota.emissor.municipio   = e.end.mun + (e.end.uf ? '/' + e.end.uf : ''); }
        if (e.end.cep)    { this.nota.emissor.cep         = e.end.cep; }
        if (e.end.compl)  { this.nota.emissor.complemento = e.end.compl; }
        if (e.end.pais)   { this.nota.emissor.pais        = e.end.pais; }
      }
    }

    if (d.tmdr) {
      const t = d.tmdr;
      if (t.razao)   { this.nota.tomador.razaoSocial  = t.razao; }
      if (t.fant)    { this.nota.tomador.nomeFantasia = t.fant; }
      if (t.cnpjFmt) { this.nota.tomador.cnpj         = t.cnpjFmt; }
      else if (t.cnpj) { this.nota.tomador.cnpj       = t.cnpj; }
      if (t.end) {
        if (t.end.lgr)    { this.nota.tomador.endereco    = t.end.lgr + (t.end.nro ? ', ' + t.end.nro : ''); }
        if (t.end.bairro) { this.nota.tomador.bairro      = t.end.bairro; }
        if (t.end.mun)    { this.nota.tomador.municipio   = t.end.mun + (t.end.uf ? '/' + t.end.uf : ''); }
        if (t.end.cep)    { this.nota.tomador.cep         = t.end.cep; }
        if (t.end.compl)  { this.nota.tomador.complemento = t.end.compl; }
        if (t.end.pais)   { this.nota.tomador.pais        = t.end.pais; }
      }
    }
    if (d.emissao)              { this.nota.emissao               = d.emissao; }
    // Vencimento real da SE2 sobrescreve o que veio do queryParam (F1_ZVENC,
    // que e' cache custom e pode estar desatualizado/vazio).
    if (d.vencimento)           { this.vencimento                 = d.vencimento; }
    if (d.valor != null)        { this.nota.impostos.total        = d.valor; }
    if (d.valorPis != null)     { this.nota.impostos.pis          = d.valorPis; }
    if (d.valorCofins != null)  { this.nota.impostos.cofins       = d.valorCofins; }
    if (d.valorIr != null)      { this.nota.impostos.ir           = d.valorIr; }
    if (d.valorCsll != null)    { this.nota.impostos.csll         = d.valorCsll; }
    if (d.valorInss != null)    { this.nota.impostos.inss         = d.valorInss; }
    if (d.observacoes)           { this.nota.observacoes           = d.observacoes; }
    if (d.observacoesAdicionais) { this.nota.observacoesAdicionais = d.observacoesAdicionais; }

    this.aplicarSelecaoProduto();
    // Em readonly (notas-entregues) os valores vem dos queryParams e do
    // backend - nao queremos que syncTipoOperacao/aplicarTesDefault
    // sobrescrevam por nao bater no filtro vigente (tipoProcesso pode estar
    // vazio nesse fluxo). Em modo edicao (notas pendentes), aplica normal.
    if (!this.isReadonly) {
      if (this.isCte) {
        // CTe: auto-pick definitivo (operador nao escolhe TES de CTe)
        this.autoPickTesCte();
      } else {
        this.syncTipoOperacao();
        // Se o backend nao retornou TES e gera-fin=sim, aplica default 257.
        this.aplicarTesDefault();
      }
    }
  }

  // ---------------------------------------------------------------------
  // Resolver do processo
  // ---------------------------------------------------------------------

  onProcessoChange(novo: string): void {
    this.processo = novo ?? '';
    // Limpa flags do processo enquanto o usuario edita - o debounce vai
    // reavaliar com o novo valor.
    this.processoEncerrado       = false;
    this.dataEncerramentoProcesso = '';
    this.processo$.next(this.processo);
  }

  private setupProcessoDebounce(): void {
    this.processo$
      .pipe(
        debounceTime(450),
        distinctUntilChanged(),
        switchMap(p => {
          const proc = (p ?? '').trim();
          if (!proc) {
            this.resolvendoProcesso = false;
            return of(null);
          }
          this.resolvendoProcesso = true;
          return this.notaDetalheService.resolverProcesso({
            processo: proc,
            filial: this.chave.filOri,
            sn:     this.simplesNacional,
            ir:    this.nota.impostos.ir,
            pis:   this.nota.impostos.pis,
            cof:   this.nota.impostos.cofins,
            csll:  this.nota.impostos.csll,
            inss:  this.nota.impostos.inss,
            total: this.nota.impostos.total,
            issRet: false,
            fin:    this.geraFinanceiro === 'sim'
          }).pipe(
            catchError(err => {
              const msg = err?.error?.message ?? 'Não foi possivel resolver o processo.';
              this.notif.warning(msg);
              return of(null);
            })
          );
        })
      )
      .subscribe((r: ResolverProcessoResp | null) => {
        this.resolvendoProcesso = false;
        if (!r) { return; }
        this.tipoProcesso = r.tipoProcesso ?? '';
        this.bu           = r.bu ?? '';
        this.centroCusto  = r.centroCusto ?? '';
        this.priCod       = r.priCod ?? '';
        if (r.tesCteOpcoes) { this.cteTesOpcoes = r.tesCteOpcoes; }
        // Nao sobrescreve a TES se o operador ja escolheu manualmente (bug 258->252).
        this.tesCalculada = r.tes ? String(r.tes).padStart(3, '0') : '';
        this.tesCalculadaDesc = r.tesDesc ?? '';
        this._tipoOperacaoOptionsKey = '';
        if (r.tes && !this.isCte && !this.tesEditadaManual) { this.tipoOperacao = String(r.tes).padStart(3, '0'); }
        if (r.natureza) { this.natureza     = r.natureza; }
        // CTe: auto-pick a TES definitiva (operador nao escolhe).
        // NFSe: sync default coerente.
        if (this.isCte) {
          this.autoPickTesCte();
        } else {
          this.syncTipoOperacao();
        }

        // Valida status do processo (backend retorna processoExiste e
        // processoEncerrado a partir da SW2/SW6.W6_DT_ENCE).
        if (r.processoExiste === false) {
          this.notif.warning('Processo nao localizado no Protheus.');
          this.processoEncerrado = false;
          this.dataEncerramentoProcesso = '';
        } else if (r.processoEncerrado) {
          this.processoEncerrado       = true;
          this.dataEncerramentoProcesso = r.dataEncerramento ?? '';
          const dt = this.dataEncerramentoProcesso
            ? ` (encerrado em ${this.formatIso(this.dataEncerramentoProcesso)})`
            : '';
          this.notif.error(`Processo encerrado${dt}. Aprovacao bloqueada.`);
        } else {
          this.processoEncerrado       = false;
          this.dataEncerramentoProcesso = '';
        }
      });
  }

  // ISO yyyy-mm-dd -> dd/mm/yyyy para exibir ao usuario.
  private formatIso(iso: string): string {
    if (!iso || iso.length < 10) return iso;
    return iso.substring(8, 10) + '/' + iso.substring(5, 7) + '/' + iso.substring(0, 4);
  }

  private carregarProdutos(): void {
    this.carregandoProdutos = true;
    this.notasAdicionarService.cnaes().subscribe({
      next: (list: CnaeOption[]) => {
        this.cnaeList = list;
        this.cnaeProdutoOptions = list.map(c => ({
          label: `${c.cnae} - ${c.descricao}`,
          value: c.produto
        }));
        this.aplicarSelecaoProduto();
        this.carregandoProdutos = false;
      },
      error: () => {
        this.notif.warning('Não foi possivel carregar a lista de produtos.');
        this.carregandoProdutos = false;
      }
    });
  }

  private aplicarSelecaoProduto(): void {
    // Em CTe o produto e decidido pela tag <indSN> (backend U_PnfPrdCte)
    // - nao tem match por CNAE.
    if (this.isCte) {
      return;
    }
    if (this.produto) {
      return;
    }
    if (!this.cnaeNota) {
      return;
    }
    const match = this.cnaeList.find(c => c.cnae.trim() === this.cnaeNota);
    if (match) {
      this.produto = match.produto;
    }
  }

  // ---------------------------------------------------------------------
  // Anexos
  // ---------------------------------------------------------------------

  carregarAnexos(): void {
    this.loadingAnexos = true;
    this.anexosService.list(this.chave).subscribe({
      next: list => {
        this.anexos = list.map(a => this.toRow(a));
        this.loadingAnexos = false;
      },
      error: () => {
        this.notif.error('Erro ao carregar anexos.');
        this.loadingAnexos = false;
      }
    });
  }

  abrirUpload(): void {
    this.filesSelected = [];
    this.gedFolderSelected = (this.gedFolderOptions[0]?.value as string | undefined) ?? '';
    this.uploadModal.open();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const accepted: Array<{ file: File; tipo: AnexoTipo | '' }> = [];
    const rejected: string[] = [];
    const duplicates: string[] = [];
    const existingNames = new Set(this.filesSelected.map(f => f.file.name));

    for (let i = 0; i < input.files.length; i++) {
      const f = input.files[i];
      if (f.size > AnexosService.MAX_BYTES) {
        rejected.push(f.name);
      } else if (existingNames.has(f.name)) {
        duplicates.push(f.name);
      } else {
        // Heuristica: pre-seleciona o tipo a partir do nome do arquivo
        // (ex: "DANFE_xxx.pdf" -> DAN, "boleto_xxx.pdf" -> BOL). Falha
        // -> tipo vazio, usuario seleciona manualmente.
        accepted.push({ file: f, tipo: this.guessTipoFromName(f.name) });
        existingNames.add(f.name);
      }
    }

    if (rejected.length > 0) {
      this.notif.warning(`Arquivo(s) excedendo 10 MB ignorado(s): ${rejected.join(', ')}`);
    }
    if (duplicates.length > 0) {
      this.notif.warning(`Arquivo(s) ja selecionado(s) ignorado(s): ${duplicates.join(', ')}`);
    }

    this.filesSelected = [...this.filesSelected, ...accepted];
    input.value = '';
  }

  // Tenta inferir o tipo do anexo a partir do nome do arquivo. Heuristica
  // best-effort - retorna '' se nada bater, ai o usuario escolhe no select.
  private guessTipoFromName(name: string): AnexoTipo | '' {
    const lower = (name || '').toLowerCase();
    // \bnf\b casa "NF" como palavra isolada (com espaco/dash/underscore em
    // volta) - cobre "NF 0123.pdf", "NF-123", "NF_123" mas NAO casa quando
    // "nf" eh parte de "nfse"/"nfe" (esses ja sao tratados explicitamente).
    if (/\bnf\b/i.test(name) || lower.includes('danfe') || lower.includes('nfse') || lower.includes('nfe')) return 'DAN';
    if (lower.includes('boleto')|| lower.includes('fatura')|| lower.includes('cobranca'))return 'BOL';
    if (lower.includes('romaneio'))                                                       return 'ROM';
    if (lower.includes('cte')   || lower.includes('conhecimento'))                        return 'CTE';
    return '';
  }

  // Remove um arquivo especifico da selecao do modal (antes de stage).
  removerArquivoSelecionado(idx: number): void {
    this.filesSelected = this.filesSelected.filter((_, i) => i !== idx);
  }

  // Stage dos anexos: nao envia agora, acumula em pendingAnexos pra ser
  // submetido quando o usuario clicar em Aprovar (vide onAprovar()).
  // Cada arquivo do filesSelected vira um PendingAnexo independente com
  // SEU PROPRIO tipo (a aprovacao em lote permite anexar varios arquivos
  // de tipos diferentes de uma vez so).
  salvarUpload(): void {
    if (this.filesSelected.length === 0) {
      this.notif.warning('Selecione pelo menos um arquivo.');
      return;
    }
    const semTipo = this.filesSelected.filter(e => !e.tipo);
    if (semTipo.length > 0) {
      this.notif.warning(`Selecione o tipo para: ${semTipo.map(e => e.file.name).join(', ')}.`);
      return;
    }

    const ged = this.gedFolderSelected;

    const novos: PendingAnexo[] = this.filesSelected.map(e => ({
      tipo: e.tipo as AnexoTipo,
      tipoLabel: this.tipoLabel(e.tipo as AnexoTipo),
      file: e.file,
      filename: e.file.name,
      tamanhoLabel: this.formatBytes(e.file.size),
      gedFolder: ged
    }));

    this.pendingAnexos = [...this.pendingAnexos, ...novos];

    this.uploadModal.close();
    this.notif.success(
      novos.length === 1
        ? 'Anexo adicionado. Sera enviado ao aprovar a nota.'
        : `${novos.length} anexos adicionados. Serao enviados ao aprovar a nota.`
    );
  }

  removerPendente(p: PendingAnexo): void {
    this.pendingAnexos = this.pendingAnexos.filter(x => x !== p);
  }

  // Limpa os arquivos selecionados no modal antes de enviar (sem fechar o modal).
  clearFileSelected(): void {
    this.filesSelected = [];
  }

  // A TES de NFS-e depende de "Gera Financeiro" e de ter ou nao processo, e a
  // lista deste componente e fixa: ela nao conhece as excecoes por filial do
  // de-para (TES 248 na 006001008, chamado 00018693). Quando o operador mexe
  // nesses radios, perguntamos ao backend qual TES vale agora - e so isso:
  // natureza, produto e retencoes continuam com o que veio no detalhe.
  private recalcularTesNfse(): void {
    if (this.isCte || this.isReadonly || !this.chave) { return; }
    this.notaDetalheService.detalhe({
      filial: this.chave.filOri,
      doc:    this.chave.numero,
      serie:  this.chave.serie,
      fornec: this.chave.fornec,
      loja:   this.chave.loja,
      emissao: this.emissaoChave || undefined,
      origem:  'pendente',
      fin:     this.geraFinanceiro === 'sim',
      issRet:  false
    }).subscribe({
      next: (d: NotaDetalheResp) => {
        this.tesCalculada = d.tes ? String(d.tes).padStart(3, '0') : '';
        this.tesCalculadaDesc = d.tesDesc ?? '';
        this._tipoOperacaoOptionsKey = '';
        // Deixa a TES da regra ja selecionada - e o que a fiscal espera ver.
        // Escolha manual do operador continua mandando (tesEditadaManual).
        if (!this.tesEditadaManual && this.tesCalculada &&
            this.tipoOperacaoOptions.some(o => o.value === this.tesCalculada)) {
          this.tipoOperacao = this.tesCalculada;
        }
      },
      // Falha aqui nao trava o operador: a lista fixa continua valendo.
      error: () => { /* silencioso */ }
    });
  }

  // Quando geraFinanceiro vira "nao", limpa o vencimento para nao manter
  // valor velho num campo que ficou desabilitado.
  // Tambem reajusta a TES selecionada conforme memo: se o usuario ja tinha
  // uma TES escolhida para o novo estado de geraFinanceiro, restaura ela
  // (desde que ainda seja valida na lista atual). Caso contrario, aplica
  // o default via defaultTesParaFin (sempre devolve um valor coerente -
  // 257/250/252/261 conforme tipoProcesso, ou 300 sem processo).
  //
  // IMPORTANTE: o set de tipoOperacao acontece num microtask depois do set
  // de geraFinanceiro pra forcar 2 ciclos de change-detection. Sem isso, o
  // po-select recebe [ngModel] novo e [p-options] novo na MESMA passagem
  // de CD, e o ngOnChanges interno dele as vezes fica com selecao vazia
  // (acontece quando o valor antigo de tipoOperacao nao existe na lista
  // nova - po-select limpa o display mesmo o ngModel ja apontando pra um
  // valor valido).
  onGeraFinanceiroChange(v: string): void {
    // Antes de trocar, memoriza a TES atual no estado anterior pra poder
    // restaurar quando o usuario voltar nesse estado.
    if (this.tipoOperacao) {
      this.tipoOperacaoMemo[this.geraFinanceiro as 'sim'|'nao'] = this.tipoOperacao;
    }
    this.geraFinanceiro = v;
    if (v !== 'sim') {
      this.vencimento = '';
      // Remove boletos staged - nota sem financeiro nao deve subir BOL
      // (regra 2026-05-29). Anexos ja persistidos ficam (operador deve
      // remover via tabela se quiser).
      this.pendingAnexos = this.pendingAnexos.filter(p => p.tipo !== 'BOL');
      // Tambem zera a selecao temporaria do modal pra nao deixar BOL
      // pendente que o tipo nao existe mais na lista filtrada.
      this.filesSelected = this.filesSelected.filter(e => e.tipo !== 'BOL');
    }
    // CTe: re-auto-pick baseado no contexto. Excecao: "Sem Processo DAAS"
    // (semProcesso=sim) mantem TES fixa em 403 indiferente do geraFinanceiro.
    // Demais cenarios: autoPickTesCte ja preserva a TES atual quando nao
    // ha match de dup (regra cliente 2026-05-28).
    if (this.isCte) {
      // Sem Processo: a planilha traz 405 (s/fin) e 407 (c/fin) na mesma
      // chamada - autoPick re-seleciona por geraFinanceiro (dup). 403 so'
      // como fallback quando a planilha nao tem linha (filial DAAS). NUNCA
      // forcar 403 com a lista preenchida - o valor sairia fora da lista e
      // o po-select ficaria vazio.
      if (this.semProcesso === 'sim' && this.cteTesOpcoes.length === 0) {
        this.tipoOperacao = '403';
        this._tipoOperacaoOptionsKey = '';
        return;
      }
      this.autoPickTesCte();
      return;
    }
    // NFSe: limpa primeiro - po-select absorve a nova [p-options] sem conflito
    // com o valor antigo (que pode nao estar mais na lista).
    this.tipoOperacao = '';
    this.recalcularTesNfse();
    // No proximo microtask, po-select ja terminou de atualizar a lista -
    // entao seta o valor final (memo ou default). Garante que a opcao
    // selecionada exista na lista no momento em que po-select faz match.
    Promise.resolve().then(() => {
      const opts = this.tipoOperacaoOptions.filter(o => o.value !== '');
      const memo = this.tipoOperacaoMemo[v as 'sim'|'nao'];
      if (memo && opts.some(o => o.value === memo)) {
        this.tipoOperacao = memo;
      } else {
        this.tipoOperacao = this.defaultTesParaFin(v, opts, this.tipoProcesso);
      }
    });
  }

  // TES default conforme o estado de geraFinanceiro + tipo do processo.
  // Espelha o mapeamento PHP NfseOperation::getTes (variante "sem issRet"
  // / "sem outRet", que sao os caminhos mais comuns - usuario pode trocar
  // pra 256/264/261/etc quando houver retencao especifica):
  //   sim + terceiros           -> 257
  //   sim + encomenda / propria -> 250
  //   nao + terceiros           -> 252 (sem ISS com retidos - regra 2026-05-27)
  //   nao + encomenda / propria -> 261 (sem ISS com retidos - regra 2026-05-27)
  // Sem processo (regra cliente 2026-05-19): TES 300 indiferente de geraFin.
  private defaultTesParaFin(v: string, opts: PoSelectOption[], tipoProcesso?: string): string {
    const tp = (tipoProcesso ?? this.tipoProcesso ?? '').toLowerCase().trim();
    if (!tp && opts.some(o => o.value === '300')) {
      return '300';
    }
    const isEncProp = tp === 'encomenda' || tp === 'propria';
    if (v === 'sim') {
      const preferida = isEncProp ? '250' : '257';
      if (opts.some(o => o.value === preferida)) { return preferida; }
      return opts.length > 0 ? String(opts[0].value) : '';
    }
    // v === 'nao'
    // IPCOT s/financeiro: default 252 (sem ISS com retidos), nao 258.
    // Motivo: o cenario comum eh ter retidos federais (PIS/COFINS/IR/CSLL)
    // mesmo quando nao ha ISS retido - a 252 cobre isso; a 258 zera tudo.
    const preferidaNao = isEncProp ? '261' : '252';
    if (opts.some(o => o.value === preferidaNao)) { return preferidaNao; }
    return opts.length > 0 ? String(opts[0].value) : '';
  }

  // Re-aplica o default da TES se o valor atual nao esta na lista coerente
  // com geraFinanceiro / tipo da nota. Idempotente: se ja estiver valido,
  // nao toca. Usa tipoProcesso corrente para escolher entre familias CF.
  private aplicarTesDefault(): void {
    this.syncTipoOperacao();
  }

  // Mantem `tipoOperacao` coerente com a lista visivel: se o valor atual
  // nao esta mais entre as opcoes validas, seleciona a unica disponivel
  // (quando ha so uma) ou limpa.
  private syncTipoOperacao(): void {
    const opts = this.tipoOperacaoOptions.filter(o => o.value !== '');
    if (!opts.some(o => o.value === this.tipoOperacao)) {
      this.tipoOperacao = this.defaultTesParaFin(this.geraFinanceiro, opts, this.tipoProcesso);
    }
  }

  baixarAnexo(a: AnexoRow): void {
    // Download via streaming autenticado (qualquer tipo, inclusive boleto/
    // romaneio que ficam privados no GCP). O backend devolve base64; aqui
    // montamos um Blob e disparamos o download local.
    this.anexosService.download(a.recno).subscribe({
      next: (d) => {
        try {
          const byteChars = atob(d.base64);
          const bytes = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) {
            bytes[i] = byteChars.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: d.contentType || 'application/octet-stream' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = d.filename || a.filename || 'anexo';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
          this.notif.error('Falha ao processar o arquivo baixado.');
        }
      },
      error: (err) => {
        const msg = err?.error?.message ?? 'Falha ao baixar o anexo.';
        this.notif.warning(msg);
      }
    });
  }

  confirmarExcluir(a: AnexoRow): void {
    this.dialog.confirm({
      title: 'Excluir anexo',
      message: `Confirma a exclusao do anexo <b>${a.filename}</b>?`,
      confirm: () => this.excluir(a)
    });
  }

  private excluir(a: AnexoRow): void {
    this.anexosService.delete(a.recno).subscribe({
      next: () => {
        this.notif.success('Anexo excluido.');
        this.anexos = this.anexos.filter(x => x.recno !== a.recno);
      },
      error: err => {
        const msg = err?.error?.message ?? 'Erro ao excluir anexo.';
        this.notif.error(msg);
      }
    });
  }

  voltar(): void {
    this.router.navigate(['/notas-list']);
  }

  // Lista exibida pelo dropdown "Produto" muda conforme o fluxo:
  //   - Oculto: cascata de Conta Contabil (ocultoProdutoOptions)
  //   - Normal: lista de produtos por CNAE (produtoOptions)
  get produtoOptionsAtivo(): PoSelectOption[] {
    return this.fornecedorOculto ? this.ocultoProdutoOptions : this.produtoOptions;
  }

  // Cascata Conta -> Produto no fluxo oculto. Chamado pelo (p-change) do
  // dropdown de Conta Contabil no template.
  onContaContabilChange(value: string): void {
    this.contaContabil = value ?? '';
    this.refreshProdutoOcultoOptions();
    // Reset produto se ele nao pertence mais a conta escolhida
    if (this.produto && !this.ocultoProdutoOptions.some(o => o.value === this.produto)) {
      this.produto = '';
    }
  }

  private refreshProdutoOcultoOptions(): void {
    this.ocultoProdutoOptions = this.dictsOcultoService.produtosPorConta(this.ocultoProdutos, this.contaContabil);
  }

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  private toRow(a: Anexo): AnexoRow {
    return {
      ...a,
      tipoLabel:       this.tipoLabel(a.tipo),
      tamanhoLabel:    this.formatBytes(a.tamanho),
      dataUploadLabel: this.formatDateTime(a.dataUpload, a.hora)
    };
  }

  private tipoLabel(t: AnexoTipo): string {
    switch (t) {
      case 'DAN': return 'Danfe';
      case 'BOL': return 'Boleto';
      case 'ROM': return 'Romaneio';
      case 'CTE': return 'CTE';
      default:    return t;
    }
  }

  private formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  }

  private formatDateTime(dataIso: string, hora: string): string {
    if (!dataIso || dataIso.length < 8) return '-';
    const dd = dataIso.slice(6, 8);
    const mm = dataIso.slice(4, 6);
    const yy = dataIso.slice(0, 4);
    return `${dd}/${mm}/${yy} ${hora || ''}`.trim();
  }

  formatEmissao(iso: string): string {
    if (!iso || iso === '-') return '-';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  }

  // Data + hora da ultima tentativa da integracao com o Conexos, ja no
  // formato da tela. '-' quando a nota ainda nao foi tentada.
  get cnxUltimaTentativa(): string {
    const cnx = this.cnxIntegracao;
    if (!cnx || !cnx.data) { return '-'; }
    const dt = this.formatEmissao(cnx.data);
    return cnx.hora ? `${dt} ${cnx.hora}` : dt;
  }

  // ---------------------------------------------------------------------
  // Reprovação (exclusão de pré-nota)
  // ---------------------------------------------------------------------

  abrirReprovar(): void {
    this.motivoReprovacao = '';
    this.reprovarModal.open();
  }

  fecharReprovar(): void {
    this.reprovarModal.close();
    this.motivoReprovacao = '';
  }

  confirmarReprovar(): void {
    if (this.reprovando) { return; }
    if (!this.chave.filOri || !this.chave.numero || !this.chave.fornec || !this.chave.loja) {
      this.notif.warning('Chave da nota incompleta.');
      return;
    }
    const req: ReprovarPendenteReq = {
      filial: this.chave.filOri,
      doc:    this.chave.numero,
      serie:  this.chave.serie,
      fornec: this.chave.fornec,
      loja:   this.chave.loja,
      motivo: this.motivoReprovacao || undefined
    };
    this.reprovando = true;
    this.notaDetalheService.reprovarPendente(req).subscribe({
      next: r => {
        this.reprovando = false;
        this.notif.success(r.message ?? 'Pre-nota reprovada com sucesso.');
        this.fecharReprovar();
        // Volta para a lista - a nota nao aparece mais nas pendentes.
        this.voltar();
      },
      error: err => {
        this.reprovando = false;
        const msg = err?.error?.message ?? 'Erro ao reprovar a pre-nota.';
        this.notif.error(msg);
      }
    });
  }

  // ---------------------------------------------------------------------
  // Aprovação
  // ---------------------------------------------------------------------

  async onAprovar(): Promise<void> {
    if (this.aprovando) { return; }
    if (!this.chave.numero || !this.chave.serie || !this.chave.fornec || !this.chave.loja) {
      this.notif.warning('Chave da nota incompleta.');
      return;
    }
    if (this.processoEncerrado) {
      const dt = this.dataEncerramentoProcesso
        ? ` (encerrado em ${this.formatIso(this.dataEncerramentoProcesso)})`
        : '';
      this.notif.error(`Processo encerrado${dt}. Aprovacao bloqueada.`);
      return;
    }
    // Processo obrigatorio salvo fornecedor oculto OU nota marcada como
    // "Sem processo". O radio so' aparece quando o backend liberou
    // (podeSemProcesso = privilegio SEMPROC OU CTe de tomador DAAS), entao
    // basta olhar o estado do radio. Espelha o gate de
    // notasPendentes:aprovaUmaCore. Chamado 00017826.
    const isSemProc = this.podeSemProcesso && this.semProcesso === 'sim';
    if (!this.processo?.trim() && !this.fornecedorOculto && !isSemProc) {
      // Quando a nota JA se qualifica para o fluxo Sem Processo DAAS (CTe +
      // tomador DAAS), a mensagem generica engana o operador: ele le
      // "processo obrigatorio" e conclui que a nota esta travada, sem
      // perceber que o radio "Sem processo" existe (fica no topo do painel,
      // normalmente fora da area visivel quando a pagina esta rolada).
      if (this.podeSemProcesso) {
        this.notif.warning(
          'Informe o Processo ou marque "Sem processo = Sim" no topo do painel ' +
          '(esta nota pode ser entregue sem processo).'
        );
      } else {
        this.notif.warning('Processo obrigatorio para fornecedor nao oculto.');
      }
      return;
    }
    if (this.geraFinanceiro === 'sim' && !this.vencimento) {
      this.notif.warning('Vencimento e obrigatorio quando Gera Financeiro = Sim.');
      return;
    }
    // Anexo da NF obrigatorio ao efetivar - regra cliente 2026-07. Documento
    // fiscal e 'DAN' (NFSe) ou 'CTE' (CTe). Aceita anexo ja salvo (ZOF/anexos)
    // ou staged agora (pendingAnexos), igual a checagem do boleto abaixo.
    const isDocNf = (t: AnexoTipo) => t === 'DAN' || t === 'CTE';
    const temDocPend = this.pendingAnexos.some(p => isDocNf(p.tipo));
    const temDocPers = this.anexos.some(a => isDocNf(a.tipo));
    if (!temDocPend && !temDocPers) {
      this.notif.warning('Anexo da NF (Danfe/CTe) eh obrigatorio para efetivar a nota.');
      return;
    }
    // Boleto obrigatorio: regra cliente 2026-05-29 (refinada). Aplica
    // somente quando geraFinanceiro=Sim AND tipoProcesso eh 'terceiros'
    // (conta e ordem) ou 'encomenda'. Filiais 'propria' / sem processo /
    // fornecedor oculto / Sem Processo DAAS NAO exigem boleto na trava
    // - sao cenarios validos sem boleto inicial.
    const exigeBoleto = this.geraFinanceiro === 'sim'
      && (this.tipoProcesso === 'terceiros' || this.tipoProcesso === 'encomenda');
    if (exigeBoleto) {
      const temBolPendente   = this.pendingAnexos.some(p => p.tipo === 'BOL');
      const temBolPersistido = this.anexos.some(a => a.tipo === 'BOL');
      if (!temBolPendente && !temBolPersistido) {
        this.notif.warning(
          'Anexo Boleto eh obrigatorio quando Gera Financeiro = Sim ' +
          'em processo Conta e Ordem ou Encomenda.'
        );
        return;
      }
    }

    const item: AprovarNotaItem = {
      filial:     this.chave.filOri,
      numero:     this.chave.numero,
      serie:      this.chave.serie,
      fornecedor: this.chave.fornec,
      loja:       this.chave.loja,
      // Troca o numero da nota na efetivacao (T... -> NF-e/limpo), igual ao
      // Executar Lote: prioriza a NF-e (numeroNfe); senao o proprio numero.
      numeroNf:   limparNumeroNf(this.numeroNfe || this.chave.numero)
    };
    if (this.processo)       { item.processo       = this.processo; }
    if (this.geraFinanceiro) { item.geraFinanceiro = this.geraFinanceiro; }
    if (this.tipoOperacao)   { item.tipoOperacao   = this.tipoOperacao; }
    if (this.vencimento)     { item.vencimento     = toIsoDateLocal(this.vencimento); }
    if (this.produto)        { item.produto        = this.produto; }
    if (this.bu)             { item.bu             = this.bu; }
    // Fluxo oculto: CC + Conta vao direto pro D1_CC/D1_CONTA sem
    // lookup SW2/CTH (backend pula esse caminho quando ZOE diz oculto).
    if (this.centroCusto)   { item.centroCusto   = this.centroCusto; }
    if (this.contaContabil) { item.contaContabil = this.contaContabil; }
    // Pedagio do CTe extraido do XML (regexPedagio). Backend deduz de
    // D1_VUNIT/D1_TOTAL e grava em F1_VALPEDG.
    if (this.valorPedagio > 0) { item.valorPedagio = this.valorPedagio; }
    // Sem processo: sinaliza pro backend. Em CTe de tomador DAAS ele forca
    // TES 403 + natureza 293014; nas demais especies exige a TES enviada
    // aqui e recusa se vier vazia. Chamado 00017826.
    if (isSemProc) { item.semProcesso = true; }

    // Gate GED: se ha anexos staged, a pasta GED de destino precisa existir no
    // Conexos ANTES de aprovar - senao o upload pos-aprovacao falha
    // silenciosamente (anexo orfao). Bloqueia se faltar. Pula notas sem
    // processo (GED-por-processo nao se aplica).
    if (this.pendingAnexos.length > 0 && this.processo) {
      const pastas = [...new Set(this.pendingAnexos.map(p => p.gedFolder).filter(Boolean))];
      for (const folder of pastas) {
        let check: GedFolderCheck;
        try {
          check = await firstValueFrom(this.anexosService.gedFolderExists({
            filOri:   this.chave.filOri,
            processo: this.processo,
            folder
          }));
        } catch {
          this.notif.warning(`Nao foi possivel validar a pasta GED "${folder}" no Conexos. Tente novamente.`);
          return;
        }
        // GED inacessivel: a checagem nao e conclusiva. Nao acusar pasta
        // faltando nem varrer as outras filiais - o erro e de acesso.
        if (check.gedStatus !== 'OK') {
          this.notif.error(gedIndisponivelMsg(check.gedStatus)!);
          return;
        }
        if (!check.exists) {
          // 2a validacao: a pasta nao existe na filial da nota, mas o mesmo
          // processo pode existir no Conexos sob OUTRA filial (o Conexos
          // resolve a pasta por processo + filial). Se achar, avisa que a nota
          // pode estar na filial errada e bloqueia a aprovacao.
          const outrasFiliais = await this.procurarPastaOutrasFiliais(folder);
          if (outrasFiliais.length > 0) {
            this.notif.warning(
              `Pasta GED "${folder}" nao existe para o processo ${this.processo} na filial ${this.chave.filOri}, ` +
              `porem o processo foi localizado no Conexos na(s) filial(is): ${outrasFiliais.join(', ')}. ` +
              `Verifique se a filial da nota esta correta.`
            );
            return;
          }
          this.notif.warning(`Pasta GED "${folder}" ainda nao existe para o processo ${this.processo}. Crie a pasta no Conexos antes de aprovar.`);
          return;
        }
      }
    }

    this.aprovando = true;
    this.notaDetalheService.aprovar({ notas: [item] }).subscribe({
      next: async resp => {
        const r = resp?.results?.[0];
        if (!r?.ok) {
          this.aprovando = false;
          this.notif.error(r?.erro ?? 'Falha ao aprovar a nota.');
          return;
        }

        // O backend pode ter efetivado a SF1 com numero diferente do "T..."
        // (numeroNf -> F1_DOC). Atualiza a chave para o doc efetivado para que
        // o upload de anexo use o numero correto e nao fique orfao.
        if (r.doc) { this.chave.numero = String(r.doc); }

        // Idem para a serie: a entrega escalona S1 -> S2 -> S3 (MV_PNFSER)
        // quando o numero ja existe para o fornecedor. O upload do anexo usa
        // this.chave.serie - sem esta atualizacao o anexo iria para a chave
        // da serie antiga e ficaria orfao.
        if (r.serie) {
          this.chave.serie = String(r.serie);
          this.nota.serie  = String(r.serie);
        }

        // Aprovou: agora sobe os anexos staged (se houver).
        if (this.pendingAnexos.length > 0) {
          const ok = await this.uploadPendentesPosAprovacao();
          this.aprovando = false;
          if (!ok) {
            // Nota JA aprovada; anexo(s) pendente(s) ja avisado(s). A nota NAO
            // e' desfeita - volta para a lista e o reenvio e' feito pela tela
            // de Notas Entregues.
            this.voltar();
            return;
          }
        } else {
          this.aprovando = false;
        }

        const infoSerie  = r.serie ? ` - série ${r.serie}` : '';
        const avisoSerie = r.aviso ? ` ${r.aviso}` : '';
        this.notif.success(`Nota aprovada${r.doc ? ' (Doc ' + r.doc + ')' : ''}${infoSerie}.${avisoSerie}`);
        this.voltar();
      },
      error: err => {
        this.aprovando = false;
        const msg = err?.error?.message ?? 'Erro ao chamar a aprovação.';
        this.notif.error(msg);
      }
    });
  }

  // Procura a pasta GED do processo nas DEMAIS filiais da empresa. O Conexos
  // resolve a pasta por processo + filial, entao a mesma referencia de
  // processo pode existir sob a arvore de outra filial. Retorna os nomes das
  // filiais onde a pasta existe (formato "codigo - nome"). Usado para alertar
  // que a nota pode estar na filial errada quando a pasta nao foi encontrada
  // na filial da propria nota. Falha em uma filial especifica nao quebra a
  // checagem (vira false) - apenas nao entra na lista.
  private async procurarPastaOutrasFiliais(folder: string): Promise<string[]> {
    let filiais = this.filialService.list();
    if (filiais.length === 0) {
      try {
        filiais = await firstValueFrom(this.filialService.loadAll());
      } catch {
        return [];
      }
    }
    const filAtual = (this.chave.filOri || '').trim();
    const candidatas = filiais.filter(f => (f.codigo || '').trim() !== filAtual && !!f.cnpj);
    if (candidatas.length === 0) {
      return [];
    }
    const resultados = await Promise.all(candidatas.map(async f => {
      try {
        const check = await firstValueFrom(this.anexosService.gedFolderExists({
          cnpjFilial: f.cnpj,
          processo:   this.processo,
          folder
        }));
        // So conta como "achei em outra filial" quando a consulta foi
        // conclusiva - com o GED fora do ar todas voltariam false.
        return check.gedStatus === 'OK' && check.exists;
      } catch {
        return false;
      }
    }));
    const achadas: string[] = [];
    candidatas.forEach((f, i) => {
      if (resultados[i]) {
        const cod = (f.codigo || '').trim();
        achadas.push(cod ? `${cod} - ${f.nome}` : f.nome);
      }
    });
    return achadas;
  }

  // Sobe os anexos staged em sequencia logo apos a aprovacao OK.
  // Em caso de falha no meio do caminho, faz ROLLBACK ATOMICO:
  // deleta anexos ja enviados (GCS + ZOF) + exclui a SF1 via MATA103 op 5.
  // O usuario recebe uma mensagem indicando que tudo foi desfeito.
  private async uploadPendentesPosAprovacao(): Promise<boolean> {
    let qtdOk = 0;
    const falhas: string[] = [];

    for (let i = 0; i < this.pendingAnexos.length; i++) {
      const p = this.pendingAnexos[i];
      try {
        const base64 = await this.anexosService.fileToBase64(p.file);
        const novo = await firstValueFrom(this.anexosService.upload({
          chave: this.chave,
          tipo: p.tipo,
          filename: p.file.name,
          contentType: p.file.type || 'application/octet-stream',
          base64,
          gedFolder: p.gedFolder || undefined,
          cnae: this.cnaeNota || undefined,
          dtVencimento: toIsoDateLocal(this.vencimento) || undefined
        }));
        this.anexos = [...this.anexos, this.toRow(novo)];
        qtdOk++;
      } catch (err: any) {
        // SEM rollback destrutivo: a nota JA esta aprovada (SF1/SE2 efetivadas,
        // podendo ter baixas parciais). Excluir a SF1 por causa de um anexo
        // falhava em BAIXAPARC e deixava a nota orfa/inconsistente. O anexo e'
        // auxiliar: registra a falha, segue com os demais e o operador reenvia
        // o(s) pendente(s) pela tela de Notas Entregues.
        const msg = err?.error?.message ?? 'falha upload';
        falhas.push(`${p.tipo} (${p.filename}): ${msg}`);
      }
    }

    this.pendingAnexos = [];

    if (falhas.length > 0) {
      this.notif.warning(
        `Nota aprovada, mas ${falhas.length} anexo(s) falhou(aram) [${qtdOk} OK]: ` +
        `${falhas.join(' | ')}. A nota NAO foi desfeita - reenvie o(s) anexo(s) ` +
        `pela tela de Notas Entregues.`
      );
      return false;
    }

    return true;
  }

}
